<template>
    <div class="live-preview-component-container">
        <div class="preview-container" :class="loading ? 'hidden-preview' : ''" ref="previewContainer"></div>
        <div class="preview-overlay" :class="loading ? '' : 'hidden-preview'">
            <div v-if="loadError" class="d-flex flex-column align-items-center">
                <font-awesome-icon style="font-size: 2rem" icon="fa-regular fa-cloud-exclamation"></font-awesome-icon>
                <p class="mt-2">Error loading preview.</p>
            </div>
            <div v-else-if="!currentSource" class="d-flex flex-column align-items-center">
                <font-awesome-icon style="font-size: 2rem" icon="fa-regular fa-cloud-exclamation"></font-awesome-icon>
                <p class="mt-2">Preview source not found.</p>
            </div>
            <div v-else class="d-flex flex-column align-items-center">
                <v-progress-circular indeterminate></v-progress-circular>
                <p class="mt-2">Loading preview...</p>
            </div>
        </div>
        <div v-if="!loading" class="preview-controls">
            <div class="control-icon" @click.stop="togglePlaying">
                <font-awesome-icon v-if="!isPlaying" icon="fa-solid fa-play"></font-awesome-icon>
                <font-awesome-icon v-else icon="fa-solid fa-pause"></font-awesome-icon>
            </div>

            <div class="d-flex flex-grow-1 align-center">
                <v-slider
                    class="mx-2"
                    @change="handleSliderChange"
                    @start="startDrag"
                    @end="endDrag"
                    v-model="currentTime"
                    :max="duration"
                    :step="dragging ? 1 : 0.1"
                    color="#ff530d"
                    track-color="#d8d8d8"
                    hide-details
                ></v-slider>

                <div class="white--text text-caption">
                    <span>
                        {{ currentTime | formatTimeStamp }}
                    </span>
                    /
                    <span>
                        {{ duration | formatTimeStamp }}
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Preview } from '@creatomate/preview';
import moment from 'moment';

export default {
    data() {
        return {
            preview: null,
            loading: true,
            loadError: false,
            isPlaying: false,
            source: this.currentSource,
            previewFont: this.customFont,
            mounted: false,
            duration: 0,
            currentTime: 0,
            dragging: false,
        };
    },
    props: {
        currentSource: {
            type: Object,
            default: false,
        },
        FirstName: {
            type: String,
            required: true,
        },
        LastName: {
            type: String,
            required: true,
        },
        BirthDate: {
            type: String,
            required: true,
        },
        DeathDate: {
            type: String,
            required: true,
        },
        customFont: {
            type: Object,
            required: false,
        },
        photo: {
            type: String,
            required: false,
        },
    },
    filters: {
        formatTimeStamp(seconds) {
            const duration = moment.duration(seconds, 'seconds');
            const minutes = duration.minutes();
            const secs = duration.seconds();

            const formattedMinutes = String(minutes).padStart(2, '0');
            const formattedSeconds = String(secs).padStart(2, '0');

            return `${formattedMinutes}:${formattedSeconds}`;
        },
    },
    watch: {
        async FirstName(newValue) {
            if (this.loading === true) return;
            this.pauseVideo();

            if (this.preview) {
                await this.preview.applyModifications({
                    FirstName: newValue,
                });
                await this.ensureElementVisibility(this.preview, 'DeathDate', 1.5);
            }
        },
        async LastName(newValue) {
            if (this.loading === true) return;
            this.pauseVideo();

            if (this.preview) {
                await this.preview.applyModifications({
                    LastName: newValue,
                });
                await this.ensureElementVisibility(this.preview, 'DeathDate', 1.5);
            }
        },
        async BirthDate(newValue) {
            if (this.loading === true) return;
            this.pauseVideo();

            if (this.preview) {
                await this.preview.applyModifications({
                    BirthDate: newValue,
                });
                await this.ensureElementVisibility(this.preview, 'DeathDate', 1.5);
            }
        },
        async DeathDate(newValue) {
            if (this.loading === true) return;
            this.pauseVideo();

            if (this.preview) {
                await this.preview.applyModifications({
                    DeathDate: newValue,
                });
                await this.ensureElementVisibility(this.preview, 'DeathDate', 1.5);
            }
        },
        async customFont(newValue) {
            await this.applyCustomFont(newValue);
        },
    },
    async mounted() {
        if (this.$props.currentSource) {
            const el = this.$refs.previewContainer;

            if (el) {
                const preview = new Preview(el, 'interactive', process.env.VUE_APP_CREATOMATE_KEY);
                this.preview = preview;
                preview.onTimeChange = time => {
                    if (!this.dragging) {
                        this.currentTime = time;
                    }
                };

                preview.onReady = async () => {
                    this.mounted = true;
                    try {
                        if (this.currentSource) {
                            let sourceCopy = JSON.parse(JSON.stringify(this.$props.currentSource));
                            await preview.setSource(sourceCopy);

                            this.duration = Math.round(sourceCopy.duration);
                        }
                        this.applyAllMods();
                        if (this.customFont) {
                            this.applyCustomFont(this.customFont);
                        }
                    } catch (error) {
                        console.log('preview player error', error);
                        this.loadError = true;
                    } finally {
                        const initialZoom = this.calculateZoom(el.clientWidth);

                        await Promise.all([this.preview.setZoom('fixed', initialZoom), this.applyAllMods()]);

                        this.loading = false;
                    }
                };

                this.resizeObserver = new ResizeObserver(entries => {
                    for (let entry of entries) {
                        if (!this.loading) {
                            const newZoom = this.calculateZoom(entry.contentRect.width);
                            this.preview.setZoom('fixed', newZoom);
                        }
                    }
                });

                this.resizeObserver.observe(el);
            }
        }
        return;
    },
    methods: {
        startDrag() {
            this.dragging = true;

            this.pauseVideo();
        },
        endDrag() {
            this.dragging = false;
        },
        handleSliderChange(number) {
            if (!this.preview) return;

            this.preview.setTime(number);
        },
        togglePlaying() {
            !this.isPlaying ? this.playVideo() : this.pauseVideo();
        },
        async applyCustomFont(newValue) {
            try {
                if (!this.mounted) return;
                if (!newValue?.family || !newValue?.files?.regular) return;

                this.previewFont = {
                    family: newValue.family,
                    source: newValue.files.regular.replace('http:', 'https:'),
                    style: 'normal',
                    weight: 700,
                };
                this.source['fonts'] = [this.previewFont];

                let sourceCopy = JSON.parse(JSON.stringify(this.source));

                if (!sourceCopy || !sourceCopy.elements) {
                    throw new Error('source copy not found');
                }

                for (const el of sourceCopy.elements) {
                    if (el.type === 'text' && typeof el.name === 'string') {
                        el.font_family = this.previewFont.family;
                        el.font_style = this.previewFont.style;
                        el.font_weight = this.previewFont.weight;
                    }
                }

                await this.preview.setSource(sourceCopy);
                await this.applyAllMods();
            } catch (error) {
                console.log('error @ font update', error);
            }
        },
        async applyAllMods() {
            if (!this.preview) return;

            return this.preview.applyModifications({
                MainPicture: this.$props.photo,
                MainPictire: this.$props.photo, //Typo in some creatomate templates
                'Main Picture': this.$props.photo, //Typo in some creatomate templates
                FirstName: this.$props.FirstName,
                LastName: this.$props.LastName,
                BirthDate: this.$props.BirthDate,
                DeathDate: this.$props.DeathDate,
            });
        },
        getPreviewSource() {
            let source = this.preview.getSource();
            return source;
        },
        async playVideo() {
            this.isPlaying = true;
            if (this.preview) {
                await this.preview.play();
            }
        },
        async pauseVideo() {
            this.isPlaying = false;
            if (this.preview) {
                await this.preview.pause();
            }
        },
        async ensureElementVisibility(preview, elementName, addTime) {
            const element = preview.getElements().find(element => element.source.name === elementName);
            if (element) {
                await preview.setTime(element.globalTime + addTime);
            }
        },
        calculateZoom(clientWidth) {
            if (clientWidth >= 670) {
                return 0.55 + ((clientWidth - 670) / (740 - 670)) * (0.6 - 0.55);
            } else if (clientWidth >= 510) {
                return 0.5 + ((clientWidth - 510) / (670 - 510)) * (0.55 - 0.5);
            } else if (clientWidth >= 460) {
                return 0.4 + ((clientWidth - 460) / (510 - 460)) * (0.5 - 0.4);
            } else if (clientWidth >= 410) {
                return 0.35 + ((clientWidth - 410) / (460 - 410)) * (0.4 - 0.35);
            } else if (clientWidth >= 340) {
                return 0.3 + ((clientWidth - 330) / (410 - 340)) * (0.35 - 0.3);
            } else {
                return 0.3;
            }
        },
    },
    beforeDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },
};
</script>

<style lang="scss">
.live-preview-component-container {
    position: relative;
    aspect-ratio: 16/9;
    background-color: black;

    .hidden-preview {
        opacity: 0;
        z-index: -2;
    }

    .control-icon {
        color: white;
        padding: 4px 8px;
        cursor: pointer;
        font-size: 1rem;
    }
}

.preview-container {
    aspect-ratio: 16/9;
    opacity: 1;
    transition: 0.5s;
}

.preview-overlay {
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 10px;
    background-color: #00000000;
    color: white;
}

.preview-controls {
    position: absolute;
    bottom: 10px;
    left: 10px;
    right: 10px;
    z-index: 10;
    display: flex;
    align-items: center;
    pointer-events: auto;
    margin: auto;

    button {
        background: none;
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    // svg {
    //     width: 28px;
    //     height: 28px;
    //     fill: white;
    //     pointer-events: auto;
    // }
}
</style>
